import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BookingEventDetail,
  BookingEventRegistrationFormValues,
  ClientOption,
  MutationResponse,
} from '@/types';
import type { TFunction } from 'i18next';

import { Form } from '@/components/Form';

import { useAppForm } from '@/hooks';

import { notifyBookingSuccess, requiredFieldIds } from '@/helpers';

import { createBookingEventRegistrationSchema } from '@/types/schemas';

import { BookingDialogBody } from '../BookingDialogBody';
import { BookingDialogFooter } from '../BookingDialogFooter';
import { ClientOrGuestField } from '../ClientOrGuestField';
import { ServiceCustomFields } from '../ServiceCustomFields';

import {
  buildRegistrationsUrl,
  createEventRegistrationPayload,
  registeredIn,
} from './utils';

type AddRegistrationFormProps = {
  event: BookingEventDetail;
  eventsUrl: string;
  clientsSearchUrl: string;
  waveId: number | null;
  onClose: () => void;
  onSuccess: () => void;
};

// No coverage picker: money for an event registration is a separate reception sale.
export const AddRegistrationForm = (props: AddRegistrationFormProps) => {
  const {
    event,
    eventsUrl,
    clientsSearchUrl,
    waveId,
    onClose,
    onSuccess: propOnSuccess,
  } = props;
  const { t } = useTranslation();

  const [client, setClient] = useState<ClientOption | null>(null);

  const customFields =
    registeredIn(event, waveId)?.service.service_custom_fields ?? [];

  const requiredCustomFieldIds = requiredFieldIds(customFields);

  const onSuccess = (response: MutationResponse) => {
    notifyBookingSuccess(response);
    propOnSuccess();
    onClose();
  };

  const { form, formFields, onSubmit, isPending } = useAppForm({
    schema: (translate: TFunction) =>
      createBookingEventRegistrationSchema(translate, requiredCustomFieldIds),
    defaultValues: {
      client_id: '',
      guest_info: '',
      note: '',
      staff_comment: '',
      custom_field_values: {},
    },
    transform: (values: BookingEventRegistrationFormValues) =>
      createEventRegistrationPayload(values, waveId),
    api_url: buildRegistrationsUrl(eventsUrl, event.id),
    redirectUrl: '',
    onSuccess,
  });

  return (
    <>
      <BookingDialogBody>
        <p className="text-sm font-medium">{t('Add registration')}</p>

        <Form
          form={form}
          onSubmit={onSubmit}
        >
          <ClientOrGuestField
            clientsSearchUrl={clientsSearchUrl}
            queryKey={['booking_event_registration_client']}
            client={client}
            onClientChange={setClient}
          />

          <ServiceCustomFields fields={customFields} />

          <formFields.Textarea
            name="note"
            label={t('Note')}
          />
          <formFields.Textarea
            name="staff_comment"
            label={t('Comment')}
          />
        </Form>
      </BookingDialogBody>

      <BookingDialogFooter
        submitLabel={t('Add registration')}
        isPending={isPending}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </>
  );
};
